import { useEffect } from 'react';
import { View, Pressable, Text, Platform, StatusBar } from 'react-native';
import { strikerRunViewStyles as styles } from './StrikerRunView.styles';
import { StrikerRunWebView } from './StrikerRunWebView';
import { StrikerRunOverlays } from './StrikerRunOverlays';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useStrikerRunGame } from '../../../src/quiz/hooks/useStrikerRunGame';
import { STRIKER_RUN_TEST_IDS } from '../../../src/quiz/constants/strikerRun.constants';

interface StrikerRunViewProps {
  onMoreGames: () => void;
}

/**
 * Fullscreen portrait host for the Striker Run runtime. Unlike Wall Breaker there is no landscape
 * rotation — the game is a portrait 720×1080 canvas that letterboxes itself into whatever box it gets.
 */
export function StrikerRunView({ onMoreGames }: StrikerRunViewProps) {
  const game = useStrikerRunGame();

  // Hide status bar on native while the game is open.
  useEffect(() => {
    if (Platform.OS !== 'web') StatusBar.setHidden(true, 'fade');
    return () => { if (Platform.OS !== 'web') StatusBar.setHidden(false, 'fade'); };
  }, []);

  // Sudden-death arcade loop — back leaves immediately (no exit summary), closing the funnel first.
  const handleBack = () => {
    game.confirmExit();
    onMoreGames();
  };

  return (
    <View style={styles.fullscreenContainer} testID={STRIKER_RUN_TEST_IDS.view}>
      {/* Back arrow — always top-left, on top of everything */}
      <Pressable
        testID={STRIKER_RUN_TEST_IDS.back}
        style={({ pressed }) => [styles.backButton, pressed && styles.pressedDim]}
        onPress={handleBack}
      >
        <Text style={styles.backArrow}>←</Text>
      </Pressable>

      <View style={styles.canvasWrap}>
        <StrikerRunWebView
          ref={(r) => game.bindWebView(r)}
          html={game.html}
          onMessage={game.onMessage}
        />
      </View>

      <StrikerRunOverlays status={game.status} onRetry={game.retry} />
      <WarmupToast message={game.toast} />
    </View>
  );
}
